import React from "react";
import { useVeltClient } from "../context";
export function useClient() {
    var client = useVeltClient().client;
    var _a = React.useState(), veltClient = _a[0], setVeltClient = _a[1];
    React.useEffect(function () {
        if (!client || veltClient)
            return;
        setVeltClient(client);
    }, [client, setVeltClient, veltClient]);
    return veltClient;
}
export function useIdentify(user, userOptions) {
    var client = useVeltClient().client;
    // Memoize the identify call
    var shouldIdentify = React.useMemo(function () {
        return { user: user, userOptions: userOptions };
    }, [JSON.stringify(user), JSON.stringify(userOptions)]);
    React.useEffect(function () {
        if (client) {
            client.identify(shouldIdentify.user, shouldIdentify.userOptions);
        }
    }, [client, shouldIdentify]);
}
function useUpdateUser(user) {
    var client = useVeltClient().client;
    React.useEffect(function () {
        client && client.updateUser(user);
    }, [client]);
}
function useSignOutUser() {
    var client = useVeltClient().client;
    React.useEffect(function () {
        client && client.signOutUser();
    }, [client]);
}
export function useSetDocument(documentId, documentMetadata) {
    var client = useVeltClient().client;
    // Memoize the setDocument call
    var shouldSetDocument = React.useMemo(function () {
        return {
            documentId: documentId,
            documentMetadata: documentMetadata
        };
    }, [documentId, JSON.stringify(documentMetadata)]);
    React.useEffect(function () {
        if (client) {
            client.setDocument(shouldSetDocument.documentId, shouldSetDocument.documentMetadata);
        }
    }, [client, shouldSetDocument]);
}
export function useSetDocumentId(documentId) {
    var client = useVeltClient().client;
    // Memoize the documentId
    var memoizedDocumentId = React.useMemo(function () { return documentId; }, [documentId]);
    React.useEffect(function () {
        if (client) {
            client.setDocumentId(memoizedDocumentId);
        }
    }, [client, memoizedDocumentId]);
}
export function useSetDocuments() {
    var client = useVeltClient().client;
    var _a = React.useState(undefined), data = _a[0], setData = _a[1];
    // Memoize the setDocuments call
    // const memoizedData = React.useMemo(() => {
    //   return data;
    // }, [JSON.stringify(data?.documents), JSON.stringify(data?.options)]);
    React.useEffect(function () {
        var _a;
        if ((client === null || client === void 0 ? void 0 : client.setDocuments) && ((_a = data === null || data === void 0 ? void 0 : data.documents) === null || _a === void 0 ? void 0 : _a.length)) {
            client.setDocuments(data.documents, data.options);
        }
    }, [client === null || client === void 0 ? void 0 : client.setDocuments, data]);
    // Memoize the setDocuments callback
    var setDocumentsCallback = React.useCallback(function (documents, options) {
        setData({ documents: documents, options: options });
    }, []); // Empty dependency array since it only uses setState which is stable
    return {
        setDocuments: setDocumentsCallback
    };
}
export function useSetRootDocument() {
    var client = useVeltClient().client;
    var _a = React.useState(undefined), data = _a[0], setData = _a[1];
    React.useEffect(function () {
        if ((client === null || client === void 0 ? void 0 : client.setRootDocument) && data) {
            client.setRootDocument(data);
        }
    }, [client === null || client === void 0 ? void 0 : client.setRootDocument, data]);
    // Memoize the setRootDocument callback
    var setRootDocumentCallback = React.useCallback(function (document) {
        setData(document);
    }, []); // Empty dependency array since it only uses setState which is stable
    return {
        setRootDocument: setRootDocumentCallback
    };
}
export function useUnsetDocumentId() {
    var client = useVeltClient().client;
    React.useEffect(function () {
        client && client.unsetDocumentId && client.unsetDocumentId();
    }, [client]);
}
export function useUnsetDocuments() {
    var client = useVeltClient().client;
    React.useEffect(function () {
        client && client.unsetDocuments && client.unsetDocuments();
    }, [client]);
}
function useGetDocumentId() {
    var client = useVeltClient().client;
    var _a = React.useState(), documentId = _a[0], setDocumentId = _a[1];
    React.useEffect(function () {
        if (!client || documentId)
            return;
        var loadedDocumentId = client.getDocumentId();
        setDocumentId(loadedDocumentId);
    }, [client, setDocumentId, documentId]);
    return documentId;
}
export function useSetLocation(location, appendLocation) {
    var client = useVeltClient().client;
    // Memoize the setLocation call
    var shouldSetLocation = React.useMemo(function () {
        return { location: location, appendLocation: appendLocation };
    }, [JSON.stringify(location), appendLocation]);
    React.useEffect(function () {
        if (client) {
            client.setLocation(shouldSetLocation.location, shouldSetLocation.appendLocation);
        }
    }, [client, shouldSetLocation]);
}
export function useSetLocations() {
    var client = useVeltClient().client;
    var _a = React.useState(undefined), data = _a[0], setData = _a[1];
    React.useEffect(function () {
        var _a;
        if ((client === null || client === void 0 ? void 0 : client.setLocations) && ((_a = data === null || data === void 0 ? void 0 : data.locations) === null || _a === void 0 ? void 0 : _a.length)) {
            client.setLocations(data.locations, data.options);
        }
    }, [client === null || client === void 0 ? void 0 : client.setLocations, data]);
    // Memoize the setLocations callback
    var setLocationsCallback = React.useCallback(function (locations, options) {
        setData({ locations: locations, options: options });
    }, []); // Empty dependency array since it only uses setState which is stable
    return {
        setLocations: setLocationsCallback
    };
}
export function useSetRootLocation() {
    var client = useVeltClient().client;
    var _a = React.useState(undefined), data = _a[0], setData = _a[1];
    React.useEffect(function () {
        if ((client === null || client === void 0 ? void 0 : client.setRootLocation) && data) {
            client.setRootLocation(data);
        }
    }, [client === null || client === void 0 ? void 0 : client.setRootLocation, data]);
    // Memoize the setRootLocation callback
    var setRootLocationCallback = React.useCallback(function (location) {
        setData(location);
    }, []); // Empty dependency array since it only uses setState which is stable
    return {
        setRootLocation: setRootLocationCallback
    };
}
export function useVeltInitState() {
    var client = useVeltClient().client;
    var _a = React.useState(), data = _a[0], setData = _a[1];
    React.useEffect(function () {
        if (!(client === null || client === void 0 ? void 0 : client.getVeltInitState))
            return;
        var subscription = client === null || client === void 0 ? void 0 : client.getVeltInitState().subscribe(function (res) {
            setData(res);
        });
        return function () {
            subscription.unsubscribe();
        };
    }, [client === null || client === void 0 ? void 0 : client.getVeltInitState]);
    return data;
}
export function useUiState() {
    var client = useVeltClient().client;
    var _a = React.useState(), uiState = _a[0], setUiStateData = _a[1];
    // Subscribe to UI state changes
    React.useEffect(function () {
        if (!(client === null || client === void 0 ? void 0 : client.getUiState))
            return;
        var subscription = client.getUiState().subscribe(function (data) {
            setUiStateData(data);
        });
        return function () {
            subscription.unsubscribe();
        };
    }, [client === null || client === void 0 ? void 0 : client.getUiState]);
    // Memoize the setUiState callback to make it stable
    var setUiState = React.useCallback(function (data) {
        if (client === null || client === void 0 ? void 0 : client.setUiState) {
            client.setUiState(data);
        }
    }, [client === null || client === void 0 ? void 0 : client.setUiState]);
    return {
        uiState: uiState,
        setUiState: setUiState
    };
}
export function useHeartbeat(heartbeatConfig) {
    var client = useVeltClient().client;
    var _a = React.useState({ data: null }), data = _a[0], setData = _a[1];
    var memoizedConfig = React.useMemo(function () { return heartbeatConfig; }, [JSON.stringify(heartbeatConfig)]);
    var subscriptionRef = React.useRef();
    React.useEffect(function () {
        if (!(client === null || client === void 0 ? void 0 : client.getHeartbeat))
            return;
        // Unsubscribe from the previous subscription if it exists
        if (subscriptionRef.current) {
            subscriptionRef.current.unsubscribe();
        }
        var subscription = client.getHeartbeat(memoizedConfig).subscribe(function (res) {
            setData(res);
        });
        // Store the new subscription
        subscriptionRef.current = subscription;
        // Cleanup function
        return function () {
            if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
            }
        };
    }, [client === null || client === void 0 ? void 0 : client.getHeartbeat, memoizedConfig]);
    return data;
}
export function useCurrentUser() {
    var client = useVeltClient().client;
    var _a = React.useState(), data = _a[0], setData = _a[1];
    var subscriptionRef = React.useRef();
    React.useEffect(function () {
        if (!(client === null || client === void 0 ? void 0 : client.getCurrentUser))
            return;
        // Unsubscribe from the previous subscription if it exists
        if (subscriptionRef.current) {
            subscriptionRef.current.unsubscribe();
        }
        var subscription = client.getCurrentUser().subscribe(function (res) {
            setData(res);
        });
        // Store the new subscription
        subscriptionRef.current = subscription;
        // Cleanup function.
        return function () {
            if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
            }
        };
    }, [client === null || client === void 0 ? void 0 : client.getCurrentUser]);
    return data;
}
export function useCurrentUserPermissions() {
    var client = useVeltClient().client;
    var _a = React.useState(), data = _a[0], setData = _a[1];
    var subscriptionRef = React.useRef();
    React.useEffect(function () {
        if (!(client === null || client === void 0 ? void 0 : client.getCurrentUserPermissions))
            return;
        // Unsubscribe from the previous subscription if it exists
        if (subscriptionRef.current) {
            subscriptionRef.current.unsubscribe();
        }
        var subscription = client.getCurrentUserPermissions().subscribe(function (res) {
            setData(res);
        });
        // Store the new subscription
        subscriptionRef.current = subscription;
        // Cleanup function.
        return function () {
            if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
            }
        };
    }, [client === null || client === void 0 ? void 0 : client.getCurrentUserPermissions]);
    return data;
}
